import type { EventType } from "@prisma/client";
import dynamic from "next/dynamic";
import { useRouter } from "next/router";
import { useEffect, useState } from "react";
import type { z } from "zod";

import type { Dayjs } from "@calcom/dayjs";
import dayjs from "@calcom/dayjs";
import DatePicker from "@calcom/features/calendars/DatePicker";
import classNames from "@calcom/lib/classNames";
import { useLocale } from "@calcom/lib/hooks/useLocale";
import type { TimeFormat } from "@calcom/lib/timeFormat";
import type { EventTypeMetaDataSchema } from "@calcom/prisma/zod-utils";
import type { Slot } from "@calcom/trpc/server/routers/viewer/slots";

import useRouterQuery from "@lib/hooks/useRouterQuery";
import type { TimeSlot } from "@lib/practice/OpendentalAdapter";

const AvailableTimes = dynamic(() => import("@components/booking/AvailableTimes"));

export const SlotPicker = ({
  eventType,
  timeFormat,
  onTimeFormatChange,
  timeZone,
  recurringEventCount,
  users,
  seatsPerTimeSlot,
  weekStart = 0,
  ethSignature,
}: {
  eventType: Pick<
    EventType & { metadata: z.infer<typeof EventTypeMetaDataSchema> },
    "id" | "schedulingType" | "slug" | "length" | "metadata"
  >;
  timeFormat: TimeFormat;
  onTimeFormatChange: (is24Hour: boolean) => void;
  timeZone?: string;
  seatsPerTimeSlot?: number;
  recurringEventCount?: number;
  users: string[];
  weekStart?: 0 | 1 | 2 | 3 | 4 | 5 | 6;
  ethSignature?: string;
}) => {
  const [selectedDate, setSelectedDate] = useState<Dayjs>();
  const [browsingDate, setBrowsingDate] = useState<Dayjs>();
  const [timeSlots, setTimeSlots] = useState<Array<TimeSlot>>([]);
  const [availableDates, setAvailableDates] = useState<string[]>([]);
  const [slots, setSlots] = useState<Array<Slot>>([]);
  const [dateSlotLoading, setDateSlotLoading] = useState(true);
  const [timeSlotLoading, setTimeSlotLoading] = useState(true);
  let { duration = eventType.length.toString() } = useRouterQuery("duration");
  const { date, setQuery: setDate } = useRouterQuery("date");
  const { month, setQuery: setMonth } = useRouterQuery("month");
  const router = useRouter();
  const { i18n, isLocaleReady } = useLocale();

  if (!eventType.metadata?.multipleDuration) {
    duration = eventType.length.toString();
  }

  useEffect(() => {
    if (!router.isReady) return;

    // Etc/GMT is not actually a timeZone, so handle this select option explicitly to prevent a hard crash.
    if (timeZone === "Etc/GMT") {
      setBrowsingDate(dayjs.utc(month).set("date", 1).set("hour", 0).set("minute", 0).set("second", 0));
      if (date) {
        setSelectedDate(dayjs.utc(date));
      }
    } else {
      // Set the start of the month without shifting time like startOf() may do.
      setBrowsingDate(
        dayjs.tz(month, timeZone).set("date", 1).set("hour", 0).set("minute", 0).set("second", 0)
      );
      if (date) {
        // It's important to set the date immediately to the timeZone, dayjs(date) will convert to browsertime.
        setSelectedDate(dayjs.tz(date, timeZone));
      }
    }
  }, [router.isReady, month, date, duration, timeZone]);

  const removeTimeQuery = () => {
    const { query } = router;
    delete query["time"];
    router.replace({ query }, undefined, { shallow: true });
  };

  useEffect(() => {
    setDateSlotLoading(true);

    const startDate = !month
      ? dayjs().tz("America/Los_Angeles").format("YYYY-MM-DD")
      : dayjs(month).startOf("month").format("YYYY-MM-DD");
    const endDate = !month
      ? dayjs().tz("America/Los_Angeles").endOf("month").format("YYYY-MM-DD")
      : dayjs(month).endOf("month").format("YYYY-MM-DD");

    removeTimeQuery();

    fetch("/api/appointments/monthslots", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        startDate,
        endDate,
      }),
    })
      .then((res) => res.json())
      .then(async (slots: TimeSlot[]) => {
        if (slots.length > 0) {
          const firstTimeSlot = dayjs(slots[0].startTime);
          await router.replace(
            {
              query: {
                ...router.query,
                date: firstTimeSlot.format("YYYY-MM-DD"),
              },
            },
            undefined,
            { shallow: true }
          );

          setTimeSlots(slots);
        }

        const dates: string[] = [];
        slots.forEach((slot) => {
          const date = dayjs(slot.startTime).format("YYYY-MM-DD");
          if (!dates.includes(date)) {
            dates.push(date);
          }
        });

        setAvailableDates(dates);
        setDateSlotLoading(false);
      });
  }, [month]);

  useEffect(() => {
    removeTimeQuery();
    setTimeSlotLoading(true);

    const todaySlots = timeSlots.filter((slot) => {
      const slotDate = dayjs(slot.startTime);
      const compareDate = dayjs(date);
      return slotDate.isSame(compareDate, "day");
    });

    const availableSlots: Slot[] = [];
    todaySlots.forEach((slot) => {
      availableSlots.push({
        time: slot.startTime,
        operatory: slot.operatoryId,
        provider: slot.providerId,
      });
    });

    setSlots(availableSlots);
    setTimeSlotLoading(false);
  }, [date, timeSlots]);

  return (
    <>
      <div>
        <h2 className="mb-2 text-xl font-bold text-gray-800">Date</h2>
        <DatePicker
          isLoading={dateSlotLoading}
          className={classNames(
            "mt-8 px-4 pb-4 sm:mt-0 md:min-w-[300px] md:px-5 lg:min-w-[455px]",
            selectedDate ? "border-gray-200 sm:border-r sm:p-4 sm:pr-6" : "sm:p-4"
          )}
          includedDates={availableDates}
          locale={isLocaleReady ? i18n.language : "en"}
          selected={selectedDate}
          onChange={(newDate) => {
            setDate(newDate.format("YYYY-MM-DD"));
          }}
          onMonthChange={(newMonth) => {
            setMonth(newMonth.format("YYYY-MM"));
          }}
          browsingDate={browsingDate}
          weekStart={weekStart}
        />
      </div>
      <div>
        <h2 className="mb-2 text-xl font-bold text-gray-800">Clock</h2>
        <AvailableTimes
          isLoading={timeSlotLoading}
          slots={selectedDate && slots}
          date={selectedDate}
          timeFormat={timeFormat}
          onTimeFormatChange={onTimeFormatChange}
          eventTypeId={eventType.id}
          eventTypeSlug={eventType.slug}
          seatsPerTimeSlot={seatsPerTimeSlot}
          recurringCount={recurringEventCount}
          ethSignature={ethSignature}
        />
      </div>
    </>
  );
};
